import React, { ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UserPlus } from 'lucide-react';

import { Button } from '@ui/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@ui/components/ui/dialog';
import { Input } from '@ui/components/ui/input';
import { Label } from '@ui/components/ui/label';
import { MultiSelectField } from '@ui/components/ui/multi-select';

import { ClientCompanyProduct, useAddClientCompanyPerson } from '@data';
import { reasonOf } from '@data/utils/errors';

const fieldLabel = 'text-xs font-semibold uppercase tracking-wider text-muted-foreground';

interface AddPersonDialogProps {
  organizationId: string;
  clientId: string;
  /** This client's products — the only things somebody here could be given to read. */
  products: ClientCompanyProduct[];
  onAdded: () => void;
}

/**
 * Giving somebody at the client an account.
 *
 * They are a client the moment this returns — no invitation to accept — which is how adding a colleague
 * already works. What they may read is picked here from this client's own products, because granting
 * access to a product we do for somebody else is never the intention.
 */
const AddPersonDialog = ({ organizationId, clientId, products, onAdded }: AddPersonDialogProps): ReactElement => {
  const { t } = useTranslation();
  const add = useAddClientCompanyPerson(organizationId, clientId);

  const [open, setOpen] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [productIds, setProductIds] = useState<string[]>(() => products.map(product => product.id));
  const [failed, setFailed] = useState<string | null>(null);

  const productOptions = products
    .map(product => ({ value: product.id, label: product.name }))
    .sort((a, b) => a.label.localeCompare(b.label));
  const allIds = products.map(product => product.id);
  // Whether the form holds the client's whole list, which is what makes the field read "All products".
  const holdsEverything = allIds.length > 0 && productIds.length === allIds.length;

  const reset = (): void => {
    setFirstName('');
    setLastName('');
    setEmail('');
    // Everything this client has: somebody added here is added to read the client's work, and taking a
    // product off them is the deliberate act rather than the default.
    setProductIds(allIds);
    setFailed(null);
  };

  const submit = async (): Promise<void> => {
    setFailed(null);
    try {
      await add.mutateAsync({
        firstName: firstName.trim(),
        lastName: lastName.trim() || undefined,
        email: email.trim(),
        productIds,
      });
      setOpen(false);
      reset();
      onAdded();
    } catch (error) {
      const reason = reasonOf(error);
      // The one refusal worth naming: this address already belongs to somebody here, which is what stops
      // a colleague being turned into a client.
      setFailed(reason === 'alreadyMember' ? t('access.alreadyMember') : t('access.addPersonFailed'));
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={next => {
        setOpen(next);
        if (!next) reset();
      }}
    >
      <Button variant="outline" size="sm" onClick={() => setOpen(true)}>
        <UserPlus className="size-4" />
        {t('access.addPerson')}
      </Button>

      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('access.addPerson')}</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-4 sm:flex-row">
            <div className="flex flex-1 flex-col gap-1.5">
              <Label className={fieldLabel} htmlFor="person-first-name">
                {`${t('firstName')} *`}
              </Label>
              <Input
                id="person-first-name"
                autoFocus
                value={firstName}
                onChange={event => setFirstName(event.target.value)}
              />
            </div>
            <div className="flex flex-1 flex-col gap-1.5">
              <Label className={fieldLabel} htmlFor="person-last-name">
                {`${t('lastName')} (${t('optional')})`}
              </Label>
              <Input id="person-last-name" value={lastName} onChange={event => setLastName(event.target.value)} />
            </div>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label className={fieldLabel} htmlFor="person-email">
              {`${t('titles.email')} *`}
            </Label>
            <Input id="person-email" type="email" value={email} onChange={event => setEmail(event.target.value)} />
          </div>

          <MultiSelectField
            label={t('access.canView')}
            options={productOptions}
            value={productIds}
            onChange={setProductIds}
            allSelected={holdsEverything}
            onToggleAll={next => setProductIds(next ? allIds : [])}
            selectAllLabel={t('allProducts')}
            emptyLabel={t('noProductsForClient')}
            searchPlaceholder={t('search')}
            emptySearchLabel={t('noMatches')}
            // On all products the field says so however many there are, rather than falling back to
            // listing them: a short client would otherwise read as a list somebody chose by hand.
            summaryThreshold={0}
            renderCountSummary={count => (holdsEverything ? t('allProducts') : t('nItemsSelected', { count }))}
          />

          <p className="text-xs text-muted-foreground">{t('access.addPersonHint')}</p>
          {failed && <p className="text-sm text-destructive">{failed}</p>}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)}>
            {t('actions.cancel')}
          </Button>
          <Button disabled={!firstName.trim() || !email.trim() || add.isPending} onClick={submit}>
            {t('access.addPerson')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AddPersonDialog;
